import {Store, Dispatch, Action} from "@reduxjs/toolkit"
import logger from "@next/core-logger"
import {IncomingHttpHeaders} from "http"
import {GetFavouritesCallbackContractModel} from "@next/eventservice"
import {createSelector} from "reselect"
import type {AxiosError} from "axios"
import {SummaryType, ColourwayData} from "../../models/searchApi"
import {
    ProductType,
    FAV_ERROR_LIMIT_EXCEEDED,
    GTM_FAVOURITES,
    GTM_ADD_FAVOURITES,
    GTM_REMOVE_FAVOURITES,
    GTM_ERROR_ADD_FAVOURITES,
    GTM_ERROR_REMOVE_FAVOURITES,
    FAV_ADD,
    FAV_REMOVE,
    SHOW_INITIAL_COLOURCHIPS,
} from "../../config/constants"
import {getProductSummary} from "../../api/getProductSummary"
import {getColourways} from "../../api/getColourways"
import {
    ProductSummaryState,
    SummaryData,
    FavErrorToolTipType,
    MobileThumbnailConfig,
    ColourChipConfig,
    AkamaiImageParams,
} from "../../models/ProductSummary"
import State from "../../models/state"
import {shoppingListItemsToFavouritedColourways} from "../../utils/favourites"
import {PublishTrackEvent} from "../../events/gtm/trackEvent"
import {getProductTitle} from "../../utils/product"
import {AdditionalColourchipsLoadingState} from "../../models/AdditionalColourchipsLoadingState"
import {ItemOptionLiteContract} from "../../models/searchApi/itemLite"
import {QUERY_PARAMETER_NEW_IN, QUERY_PARAMETER_TYPE} from "../../config/settings"

type GetStateFn = () => State

export const SET_SUMMARY_DATA = "SET_PRODUCT_SUMMARY_DATA"
export const SET_SELECTED_COLOURWAY = "SET_SELECTED_COLOURWAY"
export const SET_SELECTED_COLOURCHIP = "SET_SELECTED_COLOURCHIP"
export const ENABLE_FAV = "ENABLE_FAV"
export const SET_ENABLE_REVIEW_STARS = "SET_ENABLE_REVIEW_STARS"
export const SHOW_FAV_ERROR_TOOLTIP = "SHOW_FAV_ERROR_TOOLTIP"
export const SET_ENABLE_SEARCH_DESCRIPTION = "SET_ENABLE_SEARCH_DESCRIPTION"
export const SET_LOADING_FAV = "SET_LOADING_FAV"
export const SET_FAVOURITED_COLOURWAYS = "SET_FAVOURITED_COLOURWAYS"
export const SET_ANIMATE_FAVOURITE_ICON = "SET_ANIMATE_FAVOURITE_ICON"
export const SET_ENABLE_BRAND_DISPLAY = "SET_ENABLE_BRAND_DISPLAY"
export const SET_ENABLE_SINGLE_LINE_TITLE = "SET_ENABLE_SINGLE_LINE_TITLE"
export const SET_MOBILE_THUMBNAIL_CONFIG = "SET_MOBILE_THUMBNAIL_CONFIG"
export const SET_COLOUR_CHIP_CONFIG = "SET_COLOUR_CHIP_CONFIG"
export const SET_SALE_PRICE_TEXT_COLOR = "SET_SALE_PRICE_TEXT_COLOR"
export const SET_MAX_NUMBER_OF_SOFA_COLOURWAYS = "SET_MAX_NUMBER_OF_SOFA_COLOURWAYS"
export const SET_MAX_NUMBER_OF_HOMEWARE_COLOURWAYS = "SET_MAX_NUMBER_OF_HOMEWARE_COLOURWAYS"
export const SET_ADDITIONAL_COLOURHIPS_LOADING = "SET_ADDITIONAL_COLOURHIPS_LOADING"
export const SET_ADDITIONAL_COLOURWAYS = "SET_ADDITIONAL_COLOURWAYS"
export const SET_IMAGE_RATIO = "SET_IMAGE_RATIO"
export const SET_BRANDED_MARKEDOWN_SALE_PRICE = "SET_BRANDED_MARKEDOWN_SALE_PRICE"
export const SET_AKAMAI_IMAGE_PARAMS = "SET_AKAMAI_IMAGE_PARAMS"
export const SET_DISPLAY_FIT_ICONS = "SET_DISPLAY_FIT_ICONS"
export const SET_DISPLAY_NEW_IN = "SET_DISPLAY_NEW_IN"
export const POPULATE_PRODUCT_SUMMARY_STATE = "POPULATE_PRODUCT_SUMMARY_STATE"
export const SET_SIZE_OPTION = "SET_SIZE_OPTION"

export const initialState: ProductSummaryState = {
    summaryData: {
        colourways: [],
        id: "",
        title: "",
        brand: "",
        department: "",
        category: "",
        fit: "",
        imageCdnUrl: "",
        productImageUrlPart: "",
        lgImagePath: "",
        baseUrl: "",
        showNewIn: false,
        saleSash: null,
        saleSashPosition: null,
        type: ProductType,
        currencyCode: "",
        usePDPImage: false,
        searchApiId: "",
    },
    canLoadVisibleColourways: false,
    hasLoadedVisibleColourways: false,
    isFav: false,
    enableReviewStars: false,
    showFavErrorToolTip: null,
    enabledSearchDesc: false,
    isLoadingFav: false,
    animateFavouriteIcon: false,
    enableBrandDisplay: false,
    enableSingleLineTitle: false,
    colourchipSelected: 0,
    mobileThumbnailConfig: {
        xs: 6,
        sm: 8,
        md: 10,
    },
    colourChipConfig: {
        lg: 9,
        xl: 9,
    },
    maxNumberOfSofaColourways: null,
    maxNumberOfHomewareColourways: null,
    additionalColourchipsLoading: AdditionalColourchipsLoadingState.Default,
    numberOfAllColourways: 0,
    colourwaysHasSale: false,
    colourwaysHasRating: false,
    colourwaysHasStarRatingWithRangedPrice: false,
    imageRatio: "2x3",
    brandedMarkdownSalePrice: false,
    akamaiImageParams: {
        lg: "",
        sm: "",
    },
    displayFitIcons: true,
    displayNewIn: true,
    enableSingleLineBrandName: true,
    globalPriceFormattingConfig: {
        currencySymbol: "£",
        decimalSeparatorSymbol: ".",
        groupSeparatorSymbol: ",",
        position: "left",
        showNumOfDecimalDigits: 2,
        spaceRequired: false,
        textDirection: "ltr",
    },
    useProductAggregator: false,
    savingsSashConfig: {
        sale: 0,
        clearance: 0,
    },
    enableFurtherReductionsSash: false,
    enableQuickAddToBag: false,
    sizeOptionSelected: {
        number: "",
        displaySize: "",
    },
}

interface SetEnableSearchDescription {
    type: typeof SET_ENABLE_SEARCH_DESCRIPTION
    enabledSearchDesc: boolean
}

interface SetAkamaiImageParams {
    type: typeof SET_AKAMAI_IMAGE_PARAMS
    data: AkamaiImageParams
}

interface SetAdditionalColourchipsLoading {
    type: typeof SET_ADDITIONAL_COLOURHIPS_LOADING
    additionalColourchipsLoading: AdditionalColourchipsLoadingState
}

interface SetSummaryData {
    type: typeof SET_SUMMARY_DATA
    data: SummaryData
}

interface SetAdditionalColourways {
    type: typeof SET_ADDITIONAL_COLOURWAYS
    data: ColourwayData[]
}

interface SetLoadingFav {
    type: typeof SET_LOADING_FAV
    isLoadingFav: boolean
}

interface SetAnimateFavouriteIcon {
    type: typeof SET_ANIMATE_FAVOURITE_ICON
    animateFavouriteIcon: boolean
}

interface SetSelectedColourway {
    type: typeof SET_SELECTED_COLOURWAY
    colourwayItemNumber: string
}

interface SetSelectedColourchip {
    type: typeof SET_SELECTED_COLOURCHIP
    colourchipItemNumber: number
}

interface SetFavouritedColourways {
    type: typeof SET_FAVOURITED_COLOURWAYS
    favouritedColourways: string[]
}

interface SetEnableReviewStars {
    type: typeof SET_ENABLE_REVIEW_STARS
    enableReviewStars: boolean
}
interface SetEnableBrandDisplay {
    type: typeof SET_ENABLE_BRAND_DISPLAY
    enableBrandDisplay: boolean
}
interface SetEnableSingleLineTitle {
    type: typeof SET_ENABLE_SINGLE_LINE_TITLE
    enableSingleLineTitle: boolean
}
interface SetMobileThumbnailConfig {
    type: typeof SET_MOBILE_THUMBNAIL_CONFIG
    mobileThumbnailConfig: MobileThumbnailConfig
}

interface SetColourChipConfig {
    type: typeof SET_COLOUR_CHIP_CONFIG
    colourChipConfig: ColourChipConfig
}

interface SetMaxNumberOfSofaColourways {
    type: typeof SET_MAX_NUMBER_OF_SOFA_COLOURWAYS
    maxNumberOfSofaColourways: number | null
}

interface SetMaxNumberOfHomewareColourways {
    type: typeof SET_MAX_NUMBER_OF_HOMEWARE_COLOURWAYS
    maxNumberOfHomewareColourways: number | null
}

interface SetImageRatio {
    type: typeof SET_IMAGE_RATIO
    imageRatio: string
}

interface SetBrandedMarkdownSalePrice {
    type: typeof SET_BRANDED_MARKEDOWN_SALE_PRICE
    brandedMarkdownSalePrice: boolean
}

interface SetDisplayFitIcons {
    type: typeof SET_DISPLAY_FIT_ICONS
    displayFitIcons: boolean
}

interface SetDisplayNewIn {
    type: typeof SET_DISPLAY_NEW_IN
    displayNewIn: boolean
}

const setEnabledSearchDescription = (enabledSearchDesc: boolean) => ({
    type: SET_ENABLE_SEARCH_DESCRIPTION,
    enabledSearchDesc,
})

interface SetShowFavErrorToolTip {
    type: typeof SHOW_FAV_ERROR_TOOLTIP
    errorType: FavErrorToolTipType
}

const setProductSummaryDataAction = (data: SummaryData): SetSummaryData => {
    return {
        type: SET_SUMMARY_DATA,
        data,
    }
}

export const setAdditionalColourways = (data: ColourwayData[]): SetAdditionalColourways => {
    return {
        type: SET_ADDITIONAL_COLOURWAYS,
        data,
    }
}

export const setLoadingFav = (isLoadingFav: boolean): SetLoadingFav => {
    return {
        type: SET_LOADING_FAV,
        isLoadingFav,
    }
}

export const setAdditionalColourchipsLoading = (
    additionalColourchipsLoading: AdditionalColourchipsLoadingState,
): SetAdditionalColourchipsLoading => {
    return {
        type: SET_ADDITIONAL_COLOURHIPS_LOADING,
        additionalColourchipsLoading,
    }
}

export const setAnimateFavouriteIcon = (animateFavouriteIcon: boolean): SetAnimateFavouriteIcon => {
    return {
        type: SET_ANIMATE_FAVOURITE_ICON,
        animateFavouriteIcon,
    }
}

export const setSelectedColourwayAction = (colourwayItemNumber: string): SetSelectedColourway => {
    return {
        type: SET_SELECTED_COLOURWAY,
        colourwayItemNumber,
    }
}

export const setSelectedColourchipAction = (colourchipItemNumber: number): SetSelectedColourchip => {
    return {
        type: SET_SELECTED_COLOURCHIP,
        colourchipItemNumber,
    }
}

export function canLoadVisibleColourwaysSelector(state: {productSummary: ProductSummaryState}) {
    return state.productSummary.canLoadVisibleColourways
}

export const selectCanLoadVisibleColourways = createSelector(canLoadVisibleColourwaysSelector, val => val)

export const setEnableReviewStars = (enableReviewStars: boolean) => ({
    type: SET_ENABLE_REVIEW_STARS,
    enableReviewStars,
})
export const setEnableBrandDisplay = (enableBrandDisplay: boolean) => ({
    type: SET_ENABLE_BRAND_DISPLAY,
    enableBrandDisplay,
})

export const setEnableSingleLineTitle = (enableSingleLineTitle: boolean) => ({
    type: SET_ENABLE_SINGLE_LINE_TITLE,
    enableSingleLineTitle,
})

export const setMobileThumbnailConfig = (mobileThumbnailConfig: MobileThumbnailConfig) => ({
    type: SET_MOBILE_THUMBNAIL_CONFIG,
    mobileThumbnailConfig,
})

export const setColourChipConfig = (colourChipConfig: ColourChipConfig) => ({
    type: SET_COLOUR_CHIP_CONFIG,
    colourChipConfig,
})

export const setFavouritedColourways = (favouritedColourways: string[]) => ({
    type: SET_FAVOURITED_COLOURWAYS,
    favouritedColourways,
})

export const setShowFavErrorToolTip = (errorType: FavErrorToolTipType | null) => ({
    type: SHOW_FAV_ERROR_TOOLTIP,
    errorType,
})

export const setMaxNumberOfSofaColourways = (maxNumberOfSofaColourways: number | null) => ({
    type: SET_MAX_NUMBER_OF_SOFA_COLOURWAYS,
    maxNumberOfSofaColourways,
})

export const setMaxNumberOfHomewareColourways = (maxNumberOfHomewareColourways: number | null) => ({
    type: SET_MAX_NUMBER_OF_HOMEWARE_COLOURWAYS,
    maxNumberOfHomewareColourways,
})

export const SetImageRatio = (imageRatio: string) => ({
    type: SET_IMAGE_RATIO,
    imageRatio,
})

export const SetBrandedMarkdownSalePrice = (brandedMarkdownSalePrice: boolean) => ({
    type: SET_BRANDED_MARKEDOWN_SALE_PRICE,
    brandedMarkdownSalePrice,
})

export const SetDisplayFitIcons = (displayFitIcons: boolean) => ({
    type: SET_DISPLAY_FIT_ICONS,
    displayFitIcons,
})

export const SetDisplayNewIn = (displayNewIn: boolean) => ({
    type: SET_DISPLAY_NEW_IN,
    displayNewIn,
})
interface UpdateProductSummaryParameters {
    headers: IncomingHttpHeaders
    itemNumber: string
    type: SummaryType
    query: any
}

export const updateProductSummary = async (
    {dispatch}: Store,
    {headers, itemNumber, query, type}: UpdateProductSummaryParameters,
    enabledSearchDescription: boolean,
) => {
    try {
        const showNewIn = query[QUERY_PARAMETER_NEW_IN]
        const urlType = query[QUERY_PARAMETER_TYPE]
        const bundlePriceLabel = query.title || ""

        dispatch(setEnabledSearchDescription(enabledSearchDescription))
        const {response, responseHeaders} = await getProductSummary(
            headers,
            itemNumber,
            type,
            bundlePriceLabel,
            urlType,
        )
        response.type = response.type.toLowerCase() as SummaryType
        response.showNewIn = !!(showNewIn && showNewIn === "true")
        dispatch<Action>(setProductSummaryDataAction(response))
        return responseHeaders
    } catch (err) {
        const error = err as AxiosError
        if (error.response) {
            dispatch<Action>(setProductSummaryDataAction(initialState.summaryData))
            return {errorStatusCode: error.response.status}
        }
        logger.error(error)
        dispatch<Action>(setProductSummaryDataAction(initialState.summaryData))
    }
}

export const fetchAdditionalColourchipsThunk = () => async (dispatch: Dispatch, getState: GetStateFn) => {
    dispatch<Action>(setAdditionalColourchipsLoading(AdditionalColourchipsLoadingState.Loading))
    const state = getState()
    if (state.productSummary.summaryData.colourways.length !== state.productSummary.numberOfAllColourways) {
        try {
            const {
                summaryData: {id, searchApiId, type},
            } = state.productSummary

            const {response} = await getColourways(state.request.headers, searchApiId ?? id, type)

            dispatch<Action>(setAdditionalColourways(response))
        } catch (error) {
            logger.error(error, "fetch-additional-colourchips-thunk")
        }
    }
    dispatch<Action>(setAdditionalColourchipsLoading(AdditionalColourchipsLoadingState.Loaded))
}

export const updateFavourites =
    (getFavouritesData: GetFavouritesCallbackContractModel, favType: typeof FAV_ADD | typeof FAV_REMOVE) =>
    (dispatch: Dispatch, getState: GetStateFn) => {
        const state = getState()
        if (getFavouritesData.success) {
            dispatch<Action>(
                setFavouritedColourways(
                    shoppingListItemsToFavouritedColourways(getFavouritesData?.data?.ShoppingListItems),
                ),
            )
            dispatch<Action>(setLoadingFav(false))
            const eventActionType = favType === FAV_ADD ? GTM_ADD_FAVOURITES : GTM_REMOVE_FAVOURITES
            publishFavTrackEvent(state, eventActionType)
        } else {
            const errorType =
                getFavouritesData?.errorMessage === FAV_ERROR_LIMIT_EXCEEDED
                    ? FavErrorToolTipType.MaxLimit
                    : FavErrorToolTipType.Error
            dispatch<Action>(setShowFavErrorToolTip(errorType))
            const eventActionType = favType === FAV_ADD ? GTM_ERROR_ADD_FAVOURITES : GTM_ERROR_REMOVE_FAVOURITES

            publishFavTrackEvent(state, eventActionType)

            logger.error("Product summary: favourites api failed")
            dispatch<Action>(setLoadingFav(false))
        }
    }

export const publishFavTrackEvent = (state: State, eventActionType: string) => {
    PublishTrackEvent(GTM_FAVOURITES, {
        event: GTM_FAVOURITES,
        favourites: {
            category: GTM_FAVOURITES,
            action: eventActionType,
            label: getProductTitle(state),
            option: state.productSummary.summaryData.id,
        },
    })
}

export const updateEnablingReviewStars = (dispatch: Dispatch, enableReviewStars: boolean): any => {
    dispatch(setEnableReviewStars(enableReviewStars))
}
export const updateEnablingBrandDisplay = (dispatch: Dispatch, enableBrandDisplay: boolean): any => {
    dispatch(setEnableBrandDisplay(enableBrandDisplay))
}

export const updateMobileThumbnailConfig = (dispatch: Dispatch, mobileThumbnailConfig): any => {
    dispatch(setMobileThumbnailConfig(mobileThumbnailConfig))
}

export const updateColourChipConfig = (dispatch: Dispatch, colourChipConfig: ColourChipConfig): any => {
    dispatch(setColourChipConfig(colourChipConfig))
}

export const updateMaxNumberOfSofaColourways = (dispatch: Dispatch, maxNumberOfSofaColourways: number | null): any => {
    dispatch(setMaxNumberOfSofaColourways(maxNumberOfSofaColourways))
}

export const updateMaxNumberOfHomewareColourways = (
    dispatch: Dispatch,
    maxNumberOfHomewareColourways: number | null,
): any => {
    dispatch(setMaxNumberOfHomewareColourways(maxNumberOfHomewareColourways))
}

export const getProductId = (state: State) => {
    return state.productSummary.summaryData.id
}

export const updateImageRatio = (dispatch: Dispatch, imageRatio: string) => {
    return dispatch(SetImageRatio(imageRatio))
}

export const updateBrandedMarkdownSalePrice = (dispatch: Dispatch, brandedMarkdownSalePrice: boolean) => {
    return dispatch(SetBrandedMarkdownSalePrice(brandedMarkdownSalePrice))
}

export const updateDisplayFitIcons = (dispatch: Dispatch, displayFitIcons: boolean): any => {
    dispatch(SetDisplayFitIcons(displayFitIcons))
}

export const updateDisplayNewIn = (dispatch: Dispatch, displayNewIn: boolean): any => {
    dispatch(SetDisplayNewIn(displayNewIn))
}

interface PopulateProductSummaryState {
    type: typeof POPULATE_PRODUCT_SUMMARY_STATE
    data: Partial<ProductSummaryState>
}

interface SetSizeOptionSelected {
    type: typeof SET_SIZE_OPTION
    payload: ItemOptionLiteContract
}

export const setSizeOptionSelected = (sizeOption: ItemOptionLiteContract): SetSizeOptionSelected => ({
    type: SET_SIZE_OPTION,
    payload: sizeOption,
})

export const populateProductSummaryState = (dispatch: Dispatch, data: Partial<ProductSummaryState>) => {
    dispatch({type: POPULATE_PRODUCT_SUMMARY_STATE, data})
}

const reducer = (
    state: ProductSummaryState = initialState,
    action:
        | SetSummaryData
        | SetSelectedColourway
        | SetEnableReviewStars
        | SetShowFavErrorToolTip
        | SetEnableSearchDescription
        | SetLoadingFav
        | SetFavouritedColourways
        | SetAnimateFavouriteIcon
        | SetEnableBrandDisplay
        | SetEnableSingleLineTitle
        | SetSelectedColourchip
        | SetMobileThumbnailConfig
        | SetColourChipConfig
        | SetMaxNumberOfSofaColourways
        | SetMaxNumberOfHomewareColourways
        | SetAdditionalColourchipsLoading
        | SetAdditionalColourways
        | SetImageRatio
        | SetBrandedMarkdownSalePrice
        | SetAkamaiImageParams
        | SetDisplayFitIcons
        | SetDisplayNewIn
        | PopulateProductSummaryState
        | SetSizeOptionSelected,
): ProductSummaryState => {
    switch (action.type) {
        case SET_SUMMARY_DATA: {
            let colourways = [...action.data.colourways]
            // let associatedColourways = [...action.data.colourways.associatedColourways]
            const colourwaysHasSale = colourways.some(colourway => Boolean(colourway.sp))
            const colourwaysHasRating = colourways.some(colourway => (colourway?.st != null ? colourway.st > 0 : false))
            const colourwaysHasStarRatingWithRangedPrice = colourways.some(
                colourway => Boolean(colourway.st) && Boolean(colourway.p?.includes("-")),
            )

            if (state.maxNumberOfSofaColourways && action.data.type === "sofa") {
                colourways = action.data.colourways.slice(0, state.maxNumberOfSofaColourways)
            }
            if (state.maxNumberOfHomewareColourways && action.data.department === "Homeware") {
                colourways = action.data.colourways.slice(0, state.maxNumberOfHomewareColourways)
            }
            colourways = colourways.slice(0, SHOW_INITIAL_COLOURCHIPS)
            return {
                ...state,
                summaryData: {
                    ...action.data,
                    colourways: colourways as any[],
                },
                numberOfAllColourways: action.data.colourways.length,
                colourwaysHasSale,
                colourwaysHasRating,
                colourwaysHasStarRatingWithRangedPrice,
            }
        }
        case SET_SELECTED_COLOURWAY:
            return {
                ...state,
                summaryData: {
                    ...state.summaryData,
                    colourways: (state.summaryData.colourways as any[]).map(colourway => ({
                        ...colourway,
                        s: action.colourwayItemNumber === colourway.id,
                    })),
                },
                canLoadVisibleColourways: !state.hasLoadedVisibleColourways,
                hasLoadedVisibleColourways: true,
                isFav: !!(state.summaryData.colourways as any[]).filter(
                    colourway => action.colourwayItemNumber === colourway.id && colourway.isFav,
                ).length,
            }
        case SET_FAVOURITED_COLOURWAYS: {
            return {
                ...state,
                summaryData: {
                    ...state.summaryData,
                    colourways: (state.summaryData.colourways as any[]).map(colourway => ({
                        ...colourway,
                        isFav: action.favouritedColourways.includes(colourway.id),
                    })),
                },
                isFav: !!(state.summaryData.colourways as any[]).filter(
                    colourway => action.favouritedColourways.includes(colourway.id) && colourway.s,
                ).length,
            }
        }
        case SET_ADDITIONAL_COLOURWAYS: {
            return {
                ...state,
                summaryData: {
                    ...state.summaryData,
                    colourways: [
                        ...state.summaryData.colourways,
                        ...action.data.slice(state.summaryData.colourways.length),
                    ] as any[],
                },
            }
        }
        case SET_ENABLE_REVIEW_STARS:
            return {...state, enableReviewStars: action.enableReviewStars}
        case SET_ENABLE_BRAND_DISPLAY:
            return {...state, enableBrandDisplay: action.enableBrandDisplay}
        case SET_ENABLE_SINGLE_LINE_TITLE:
            return {...state, enableSingleLineTitle: action.enableSingleLineTitle}
        case SET_LOADING_FAV:
            return {...state, isLoadingFav: action.isLoadingFav}
        case SET_ANIMATE_FAVOURITE_ICON:
            return {...state, animateFavouriteIcon: action.animateFavouriteIcon}
        case SHOW_FAV_ERROR_TOOLTIP:
            return {...state, showFavErrorToolTip: action.errorType}
        case SET_ENABLE_SEARCH_DESCRIPTION:
            return {...state, enabledSearchDesc: action.enabledSearchDesc}
        case SET_SELECTED_COLOURCHIP:
            return {...state, colourchipSelected: action.colourchipItemNumber}
        case SET_MOBILE_THUMBNAIL_CONFIG:
            return {...state, mobileThumbnailConfig: action.mobileThumbnailConfig}
        case SET_COLOUR_CHIP_CONFIG:
            return {...state, colourChipConfig: action.colourChipConfig}
        case SET_MAX_NUMBER_OF_SOFA_COLOURWAYS:
            return {...state, maxNumberOfSofaColourways: action.maxNumberOfSofaColourways}
        case SET_MAX_NUMBER_OF_HOMEWARE_COLOURWAYS:
            return {...state, maxNumberOfHomewareColourways: action.maxNumberOfHomewareColourways}
        case SET_ADDITIONAL_COLOURHIPS_LOADING:
            return {...state, additionalColourchipsLoading: action.additionalColourchipsLoading}
        case SET_IMAGE_RATIO:
            return {...state, imageRatio: action.imageRatio}
        case SET_BRANDED_MARKEDOWN_SALE_PRICE:
            return {...state, brandedMarkdownSalePrice: action.brandedMarkdownSalePrice}
        case SET_AKAMAI_IMAGE_PARAMS:
            return {...state, akamaiImageParams: action.data}
        case SET_DISPLAY_FIT_ICONS:
            return {...state, displayFitIcons: action.displayFitIcons}
        case SET_DISPLAY_NEW_IN:
            return {...state, displayNewIn: action.displayNewIn}
        case POPULATE_PRODUCT_SUMMARY_STATE:
            return {...state, ...action.data}
        case SET_SIZE_OPTION:
            return {
                ...state,
                sizeOptionSelected: action.payload,
            }

        default:
            return state
    }
}

export default reducer
